import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject, input } from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {MenuService} from './app.menu.service';
import {AppComponent} from './app.component';
import {AppMainComponent} from './app.main.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    template: `
        <ng-container>
          @if (root() && item.visible !== false) {
            <div class="layout-menuitem-root-text">{{item.label | translate}}
              @if ((!appMain.sidebarActive && app.layoutMode !== 'static' && !appMain.isMobile())) {
                <i class="pi pi-ellipsis-h"></i>
              }
            </div>
          }
          @if ((!item.routerLink || item.items) && item.visible !== false) {
            <a [attr.href]="item.url" (click)="itemClick($event)" (mouseenter)="onMouseEnter()"
              (keydown.enter)="itemClick($event)"
              [attr.target]="item.target" [attr.tabindex]="0" [ngClass]="item.class" pRipple>
              <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
              <span class="menuitem-text">{{item.label | translate}}</span>
              @if (item.items) {
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
              }
              @if (item.badge) {
                <span class="menuitem-badge">{{item.badge}}</span>
              }
            </a>
          }
          @if ((item.routerLink && !item.items) && item.visible !== false) {
            <a (click)="itemClick($event)" (mouseenter)="onMouseEnter()"
              [routerLink]="item.routerLink" routerLinkActive="active-menuitem-routerlink" [ngClass]="item.class" pRipple
              [routerLinkActiveOptions]="{exact: !item.preventExact}" [attr.target]="item.target" [attr.tabindex]="0">
              <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
              <span class="menuitem-text">{{item.label | translate}}</span>
              @if (item.items) {
                <i class="pi pi-fw pi-angle-down layout-submenu-toggler">keyboard_arrow_down</i>
              }
              @if (item.badge) {
                <span class="menuitem-badge">{{item.badge}}</span>
              }
            </a>
          }
          @if ((item.items || active) && item.visible !== false) {
            <ul
              [@children]="(appMain.isHorizontal() && !appMain.isMobile() && root()) ? (active ? 'visible' : 'hidden') : (root() ? 'visible' :active ? 'visibleAnimated' : 'hiddenAnimated')">
              @for (child of item.items; track child; let i = $index) {
                <li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child.badgeClass"></li>
              }
            </ul>
          }
        </ng-container>
        `,
    host: {
        '[class.layout-root-menuitem]': 'root()',
        '[class.active-menuitem]': 'active'
    },
    animations: [
        trigger('children', [
            state('void', style({
                height: '0px'
            })),
            state('hiddenAnimated', style({
                height: '0px'
            })),
            state('visibleAnimated', style({
                height: '*'
            })),
            state('visible', style({
                height: '*',
                'z-index': 100
            })),
            state('hidden', style({
                height: '0px',
                'z-index': '*'
            })),
            transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('void => visibleAnimated, visibleAnimated => void', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
    app = inject(AppComponent);
    appMain = inject(AppMainComponent);
    router = inject(Router);
    private menuService = inject(MenuService);


    @Input() item: any;

    readonly index = input<number>(undefined);

    readonly root = input<boolean>(undefined);

    readonly parentKey = input<string>(undefined);

    active = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    key: string;

    constructor() {
        this.menuSourceSubscription = this.menuService.menuSource$.subscribe(key => {
            // deactivate current active menu
            if (this.active && this.key !== key && key.indexOf(this.key) !== 0) {
                this.active = false;
            }
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(params => {
                if (this.appMain.isHorizontal()) {
                    this.active = false;
                } else {
                    if (this.item.routerLink) {
                        this.updateActiveStateFromRoute();
                    } else {
                        this.active = false;
                    }
                }
            });
    }

    ngOnInit() {
        if (!this.appMain.isHorizontal() && this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }

        const parentKey = this.parentKey();
        this.key = parentKey ? parentKey + '-' + this.index() : String(this.index());
    }

    updateActiveStateFromRoute() {
        this.active = this.router.isActive(this.item.routerLink[0], !this.item.items && !this.item.preventExact);
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover in horizontal mode
        if (this.root()) {
            this.appMain.menuHoverActive = !this.appMain.menuHoverActive;
        }

        // notify other items
        this.menuService.onMenuStateChange(this.key);

        // execute command
        if (this.item.command) {
            this.item.command({originalEvent: event, item: this.item});
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;
        } else {
            // activate item
            this.active = true;

            // hide overlay menus
            if (this.appMain.isMobile()) {
                this.appMain.sidebarActive = false;
                this.appMain.mobileMenuActive = false;
            }

            // reset horizontal menu
            if (this.appMain.isHorizontal()) {
                this.menuService.reset();
                this.appMain.menuHoverActive = false;
            }
        }
    }

    onMouseEnter() {
        // activate item on hover
        if (this.root() && this.appMain.menuHoverActive && this.appMain.isHorizontal() && this.appMain.isDesktop()) {
            this.menuService.onMenuStateChange(this.key);
            this.active = true;
        }
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
